import { CommonModule } from '@angular/common';
import { ScrollingModule, CdkVirtualScrollViewport } from '@angular/cdk/scrolling';
import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  HostListener,
  inject,
  Input,
  NgZone,
  OnDestroy,
  Output,
  ViewChild,
} from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatSelect, MatSelectModule } from '@angular/material/select';
import { MatTabsModule } from '@angular/material/tabs';
import { ActivatedRoute, Router } from '@angular/router';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { CalendarModule } from 'primeng/calendar';
import { CheckboxModule } from 'primeng/checkbox';
import { DialogModule } from 'primeng/dialog';
import { OverlayPanel, OverlayPanelModule } from 'primeng/overlaypanel';
import { SidebarModule } from 'primeng/sidebar';
import { TabViewModule } from 'primeng/tabview';
import { Subject, Subscription, debounceTime, distinctUntilChanged, takeUntil } from 'rxjs';
import { event_status } from '../../../../assets/constData/event_status';
import {
  buildEventDetailRouteSegments,
  resolveEventLandingTabId,
} from '../../../utilities/event-landing-tab/event-landing-tab.util';
import { environment } from '../../../../environments/environment';
import { CommonServiceService } from '../../../utilities/services/common-service/common-service.service';
import { EncryptDecryptService } from '../../../utilities/services/EncrptyDecrypt/encrypt-decrypt.service';
import {
  EventsFilterState,
  EventsFilterStateService,
} from '../../../utilities/services/events-filter-state/events-filter-state.service';
import { OrderHistoryService } from '../../../utilities/services/order-history/order-history.service';
import { PointLeaderboardService } from '../../../utilities/services/point-leaderboard/point-leaderboard.service';
import { TemplateService } from '../../../utilities/services/template/template.service';
import { ThemeServiceService } from '../../../utilities/services/theme-service.service';
import { SubLicenseManagementService } from '../../sub-licenses/sub-license-management/sub-license-management.service';
import { extractEventVenueLocation, GeoPoint, MapBounds } from './event-discovery/event-map-geo';
import { eventDistanceKm, sortEventsByDistance } from './event-discovery/event-near-me';
import { CommonSearchBarComponent } from '../../shared/common-search-bar/common-search-bar.component';
import { EmptyStateComponent } from '../../shared/empty-state/empty-state.component';
import { SkeletonLoaderComponent } from '../../shared/skeleton-loader/skeleton-loader.component';
import { GifLoaderComponent } from '../../shared/gif-loader/gif-loader.component';
import { MobileSearchComponent } from '../../shared/mobile-search/mobile-search.component';
import { TournamentCardComponent } from '../../shared/tournament-card/tournament-card.component';
import { EventDiscoveryFilterDrawerComponent } from './event-discovery/event-discovery-filter-drawer.component';
import { EventMapViewComponent } from './event-discovery/event-map-view.component';
import { EventListCardComponent } from './event-list-card/event-list-card.component';
import {
  Template1EventCardComponent,
  Template1EventItem,
  VISUAL_STATUS_BY_VALUE,
} from './template1-event-card/template1-event-card.component';
import {
  EMPTY_ORGANISER_DETAILS,
  extractOrganiserDetails,
  OrganiserDetails,
} from '../../../models/event/organiser-details';

/** Quick ranges shown above the mobile date calendar. */
type DiscoveryDatePreset = 'any' | 'lastWeek' | 'thisWeek' | 'nextWeek';

@Component({
  selector: 'stupa-all-events',
  standalone: true,
  imports: [
    CommonModule,
    TranslateModule,
    TabViewModule,
    MatTabsModule,
    FormsModule,
    ReactiveFormsModule,
    CommonSearchBarComponent,
    OverlayPanelModule,
    CheckboxModule,
    EventListCardComponent,
    MobileSearchComponent,
    SkeletonLoaderComponent,
    GifLoaderComponent,
    EmptyStateComponent,
    CalendarModule,
    DialogModule,
    MatSelectModule,
    SidebarModule,
    TournamentCardComponent,
    EventMapViewComponent,
    EventDiscoveryFilterDrawerComponent,
    Template1EventCardComponent,
    ScrollingModule,
  ],
  templateUrl: './all-events.component.html',
  styleUrl: './all-events.component.scss',
})
export class AllEventsComponent implements AfterViewInit, OnDestroy {
  @Input() is_sub_license: boolean = false;
  @Input() user_role_id: any = null;
  @Output() event_tab_index: EventEmitter<number> = new EventEmitter<number>();
  events_list: any = [];
  displayedEvents: any[] = [];
  tabs: any = [
    { id: 1, title: 'all', event_type: '' },
    { id: 2, title: 'tournaments', event_type: 'T' },
    { id: 3, title: 'leagues', event_type: 'L' },
  ];

  tabs_broadcasts: any = [
    { id: 4, title: 'all', event_type: '' },
    { id: 5, title: 'tournaments', event_type: 'BT' },
    { id: 6, title: 'leagues', event_type: 'BL' },
  ];
  selected_tab: any;
  searchQuery: string = '';
  searchTimeout: any;
  total_events: any;
  error_message: any = '';
  screenWidth: any;
  event_statuses: any = event_status;
  selectedStatus: any = [];
  viewMode: 'grid' | 'list' | 'map' = 'grid';
  get grid_view(): boolean {
    return this.viewMode === 'grid';
  }
  /** Synced from stupa-common-search-bar when inline search expands/collapses (narrow layout). Desktop tabs hide while expanded >1024. */
  searchBarExpanded = false;

  // Pagination properties
  currentPage: number = 1;
  pageSize: number = 12;
  /** Map view filters by the frame, so one page has to cover nearby cities. */
  private static readonly MAP_PAGE_SIZE = 100;
  totalPages: number = 0;
  hasMoreData: boolean = true;
  isLoading: boolean = true;
  readonly skeletonCards = [0, 1, 2, 3, 4, 5];
  currentEventType: string = '';
  currentEventStatus: string = '';
  currentSearch: string = '';

  // Debouncing properties
  private searchSubject = new Subject<string>();
  private destroy$ = new Subject<void>();

  // Theme property
  is_dark: boolean = false;
  nearMeOrigin: GeoPoint | null = null;
  nearMePending = false;
  private nearMeRequest = 0;
  /** Last map frame sent to get_events. Null outside map view. */
  private mapViewport: MapBounds | null = null;
  private listRequest = 0;
  private readonly snackBar = inject(MatSnackBar);

  // Route tracking
  isBroadcastRoute: boolean = false;

  /** Mirrors `$t1-bp-mobile` in `_template1-tokens.scss` — keep the two in step. */
  private static readonly T1_MOBILE_BP = 880;

  /** Template-1 phone layout: the filter pills live in a bottom drawer there. */
  get isT1Mobile(): boolean {
    return this.screenWidth <= AllEventsComponent.T1_MOBILE_BP;
  }

  /** Whether the template-1 phone filter drawer is showing. */
  t1MobileFiltersOpen = false;

  /**
   * Placeholder rows for the loading skeleton, shown while `isLoading` is true and
   * `events_list` is still empty — the initial page load and every search/filter
   * change, none of which the existing `t1-ev-loading` "loading more" indicator
   * covers (that one requires `events_list.length > 0`). Four rows fills roughly
   * one phone screen without scrolling.
   */
  readonly t1SkeletonCards: ReadonlyArray<number> = [0, 1, 2, 3];

  /**
   * Reference build's phone grouping: a flat list of fifteen 188px cards asks
   * the reader to hold every status in their head while scrolling. Recent and
   * Completed share one bucket - both are "already happened" to someone
   * scanning, and the status chips still tell them apart.
   */
  private static readonly T1_GROUPS: ReadonlyArray<{
    readonly key: string;
    readonly label: string;
    readonly color: string;
  }> = [
    { key: 'ongoing', label: 'template1_events_group_now', color: '#10b981' },
    { key: 'upcoming', label: 'template1_events_group_upcoming', color: '#3b82f6' },
    { key: 'past', label: 'template1_events_group_past', color: '#9ca3af' },
  ];

  /**
   * The card list, bucketed by status for the phone layout. Above the phone
   * breakpoint it stays a single unlabelled group, so the template keeps one
   * loop and desktop keeps the server's ordering untouched.
   */
  get t1EventGroups(): ReadonlyArray<{
    key: string;
    label: string;
    color: string;
    items: Template1EventItem[];
  }> {
    const list: Template1EventItem[] = this.events_list ?? [];
    if (!this.isT1Mobile) {
      return [{ key: 'all', label: '', color: '', items: list }];
    }
    return AllEventsComponent.T1_GROUPS.map((g) => ({
      ...g,
      items: list.filter((e) => this.t1GroupKeyOf(e) === g.key),
    })).filter((g) => g.items.length > 0);
  }

  /**
   * Which bucket an event belongs to. Reuses the card's own status map rather
   * than a second copy, so a status added there groups correctly here too.
   */
  private t1GroupKeyOf(event: Template1EventItem): string {
    const visual = VISUAL_STATUS_BY_VALUE[Number(event?.event_status)];
    return visual === 'ongoing' || visual === 'upcoming' ? visual : 'past';
  }

  /**
   * Filters currently narrowing the list — the badge on the phone Filters
   * button, so an active filter is visible while the drawer is closed.
   *
   * @returns Count of applied filters (the date range counts once).
   */
  get activeFilterCount(): number {
    return [
      this.selectedEventStatus.length > 0,
      this.selectedSeason.length > 0,
      this.selectedEventLevel.length > 0,
      !!(this.appliedStartDate || this.appliedEndDate),
    ].filter(Boolean).length;
  }

  @HostListener('window:resize', ['$event'])
  onResize(event: Event) {
    this.screenWidth = window.innerWidth;
    // Leaving the phone layout moves the pills back into the bar, so a drawer
    // left open would strand an empty sheet over the page.
    if (!this.isT1Mobile) {
      this.t1MobileFiltersOpen = false;
    }
    if (this.screenWidth > 1024 && this.searchBarExpanded) {
      /* Desktop: collapsed search chrome is inactive; tabs can show again. Child may still think expanded until next toggle—clear parent flag only when leaving narrow. */
      this.searchBarExpanded = false;
    }
    if (this.screenWidth > 1024) {
      this.mobileFiltersOpen = false;
    }
    // Update visible months when screen size changes
    if (this.visibleMonths && this.visibleMonths.length > 0) {
      this.updateVisibleMonths();
    }
  }

  /**
   * Registered outside Angular's zone (see ngOnInit): a @HostListener('window:scroll') ran change
   * detection for the whole app on every scroll event, which starved the virtual grid of frames on a
   * fast scroll and left unrendered gaps. Angular is only re-entered when a page actually loads.
   */
  private readonly onWindowScroll = () => {
    if (this.viewMode === 'map' || this.isLoading || !this.hasMoreData) return;

    const windowHeight = window.innerHeight;
    const documentHeight = document.documentElement.scrollHeight;
    const scrollTop = window.pageYOffset || document.documentElement.scrollTop;

    // Load more when user scrolls to 80% of the page
    if (scrollTop + windowHeight >= documentHeight * 0.8) {
      this.ngZone.run(() => this.loadMoreEvents());
    }
  };

  ngOnInit() {
    this.ngZone.runOutsideAngular(() =>
      window.addEventListener('scroll', this.onWindowScroll, { passive: true }),
    );

    // Initialize screen width when component loads
    this.screenWidth = window.innerWidth;

    // Setup debounced search
    this.setupDebouncedSearch();

    // Subscribe to theme changes
    this.themeService.isDark$
      .pipe(takeUntil(this.destroy$))
      .subscribe((isDark: boolean) => {
        this.is_dark = isDark;
      });

    // Initialize theme value
    this.is_dark = this.themeService.getTheme();

    this.weekDays = this.getLocalizedWeekDays();

    this.translateService.onLangChange
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.weekDays = this.getLocalizedWeekDays();
        this.updateVisibleMonths();
      });

    // Initialize component with is_sub_license value (inputs are now available)
    this.selected_tab = this.is_sub_license
      ? Number(this.activatedRoute.snapshot.paramMap.get('event_tab_index')) || 0
      : Number(this.activatedRoute.snapshot.paramMap.get('id'));
    const currentRoute = window.location.href.split('/')[3];
    this.isBroadcastRoute = this.is_sub_license
      ? false
      : currentRoute === 'broadcasts';

    this.eventsFilterStateService.resetOnContextChange(
      this.isBroadcastRoute ? 'broadcasts' : 'events',
    );
    this.loadFilters();
    if (this.isBroadcastRoute) {
      this.tabs = [...this.tabs_broadcasts];
      const broadcastTab = this.tabs_broadcasts.filter(
        (x: any) => x.id == this.selected_tab,
      )[0];
      const broadcastEventType = broadcastTab?.event_type ?? '';
      this.get_sections(broadcastEventType);
      this.get_broadcasts(
        broadcastEventType,
        false,
        this.searchQuery || null,
        this.selectedEventStatus || null,
      );
    } else {
      this.get_seasons(() => {
        this.get_all_events(
          this.tabs.filter((x: any) => x.id == this.selected_tab)[0].event_type,
          this.selectedEventStatus || '',
          this.searchQuery || '',
        );
      });
    }
  }

  ngOnDestroy() {
    window.removeEventListener('scroll', this.onWindowScroll);
    this.gridResizeObserver?.disconnect();
    this.gridRangeSubscription?.unsubscribe();
    if (this.isTemplate1) {
      this.eventsFilterStateService.clearFilters();
    }
    this.destroy$.next();
    this.destroy$.complete();
  }

  private loadFilters(): void {
    const filters = this.eventsFilterStateService.restoreFilters();
    this.applyFilters(filters);
  }

  private applyFilters(f: EventsFilterState): void {
    this.selectedEventStatus = this.parseFilterList(f.status);
    this.pendingEventStatus = [...this.selectedEventStatus];
    this.selectedSeason = this.parseFilterList(f.season);
    this.pendingSeason = [...this.selectedSeason];
    this.selectedEventLevel = this.parseEventLevelFilters(f.level);
    this.pendingEventLevel = [...this.selectedEventLevel];
    this.appliedStartDate = f.startDate || '';
    this.appliedEndDate = f.endDate || '';
    const search = f.search?.trim() || '';
    if (search !== this.searchQuery) {
      this.searchQuery = search;
      this.searchSubject.next(search);
    }
  }

  private saveFilters(): void {
    this.eventsFilterStateService.persistFilters({
      status: this.toFilterParam(this.selectedEventStatus),
      season: this.toFilterParam(this.selectedSeason),
      level: this.toFilterParam(this.selectedEventLevel),
      startDate: this.appliedStartDate,
      endDate: this.appliedEndDate,
      search: this.searchQuery,
    });
  }

  private toFilterParam(value: unknown): string {
    if (Array.isArray(value)) {
      return value.length > 0 ? value.map(String).join(',') : '';
    }
    if (value == null || value === '') {
      return '';
    }
    return String(value);
  }

  private resolveEventLevelLabel(item: string): string {
    const normalized = String(item).trim().toLowerCase();
    const match = this.eventLevelOptions.find(
      (level) =>
        String(level.value).toLowerCase() === normalized ||
        String(level.label).toLowerCase() === normalized,
    );
    return match?.label ?? item;
  }

  /** BE expects Title Case labels, e.g. National,District */
  private toEventLevelParam(): string {
    if (!this.selectedEventLevel.length) {
      return '';
    }
    return this.selectedEventLevel.map((item) => this.resolveEventLevelLabel(item)).join(',');
  }

  private parseFilterList(raw: string): string[] {
    if (!raw?.trim()) {
      return [];
    }
    return raw
      .split(',')
      .map((item) => item.trim())
      .filter((item) => item.length > 0);
  }

  private parseEventLevelFilters(raw: string): string[] {
    return this.parseFilterList(raw).map((item) => this.resolveEventLevelLabel(item));
  }

  readonly compareFilterValues = (a: unknown, b: unknown): boolean =>
    String(a) === String(b);

  private setupDebouncedSearch() {
    this.searchSubject
      .pipe(
        debounceTime(500), // Wait 500ms after user stops typing
        distinctUntilChanged(), // Only emit if value has changed
        takeUntil(this.destroy$),
      )
      .subscribe((searchTerm: string) => {
        this.performSearch(searchTerm);
      });
  }

  private performSearch(searchTerm: string) {
    // Update the search query property
    this.searchQuery = searchTerm || '';
    this.saveFilters();

    // Always reset pagination for any search
    this.currentPage = 1;
    this.setEventsList([]);
    this.hasMoreData = true;

    // Check if we're on broadcasts route
    const currentRoute = window.location.href.split('/')[3];
    const isBroadcastRoute = this.is_sub_license
      ? false
      : currentRoute === 'broadcasts';

    // Check if search term is empty, null, or only whitespace
    if (searchTerm && searchTerm.trim().length > 0) {
      if (isBroadcastRoute) {
        // For broadcasts, search functionality might need to be implemented in get_broadcasts
        // For now, call get_broadcasts with current tab's event_type
        this.get_broadcasts(
          this.tabs_broadcasts.filter((x: any) => x.id == this.selected_tab)[0]
            .event_type,
          false,
          searchTerm.trim(),
          this.selectedEventStatus,
        );
      } else {
        // Perform search with the search term while maintaining current filters
        this.get_all_events(
          this.currentEventType,
          this.currentEventStatus,
          searchTerm.trim(),
        );
      }
    } else {
      // Reset to current tab's events when search is cleared or empty
      if (isBroadcastRoute) {
        this.get_broadcasts(
          this.tabs_broadcasts.filter((x: any) => x.id == this.selected_tab)[0]
            .event_type,
          false,
          null,
          this.selectedEventStatus,
        );
      } else {
        this.get_all_events(
          this.tabs.filter((x: any) => x.id == this.selected_tab)[0].event_type,
          this.currentEventStatus,
          '',
        );
      }
    }
  }

  selectedTournamentId: any;
  constructor(
    private commonServiceService: CommonServiceService,
    private activatedRoute: ActivatedRoute,
    private router: Router,
    private cdr: ChangeDetectorRef,
    private pointLeaderboardService: PointLeaderboardService,
    private themeService: ThemeServiceService,
    private translateService: TranslateService,
    private encryptDecryptService: EncryptDecryptService,
    private orderService: OrderHistoryService,
    private subLicenseManagementService: SubLicenseManagementService,
    private eventsFilterStateService: EventsFilterStateService,
    private readonly templateService: TemplateService,
  ) {
    this.checkRole();
  }

  ngAfterViewInit(): void {
    this.resetVisibleMonthsToCurrentDate();
    this.updateVisibleMonths();
  }

  /**
   * True when the OTT layout should render.
   *
   * Only the chrome branches on this — every fetch, filter and click handler
   * below is shared by both templates, so the two designs cannot drift apart.
   */
  get isTemplate1(): boolean {
    return this.templateService.isTemplate1;
  }

  /**
   * Overlay class for the template-1 filter dropdowns.
   *
   * The mat-select panel renders in a CDK overlay outside this component, so
   * the OTT glass styling can only reach it through a panel class. Dark mode
   * is not a separate class here — `.t1-csel-panel` is styled entirely with
   * `--t1-*` tokens, which already flip on `body.dark-theme` (the classic
   * `dark-theme-dropdown` class hardcodes its own colours and would only
   * "win" by cascade order, not by design).
   *
   * @returns Panel classes for the template-1 filter overlay.
   */
  get t1SelectPanelClass(): string[] {
    return ['t1-csel-panel'];
  }

  /**
   * Returns indicator dot color for Template 1 status dropdown matching LA48AF_1_updated.HTM spec.
   * Ongoing -> #10B981, Upcoming/Open -> #3B82F6, Recent -> #6B7280, Completed/Closed -> #374151.
   */
  getStatusDotColor(status: any): string {
    if (!status) return '';
    const val = status.status_id ?? status.value ?? status.id ?? status;
    const title = (status.status_description || status.title || status.class_name || '').toString().toUpperCase();
    if (val === 13 || title.includes('ONGOING')) return '#10B981';
    if (val === 14 || val === 1 || title.includes('UPCOMING') || title.includes('REGISTRATION OPEN')) return '#3B82F6';
    if (val === 15 || title.includes('RECENT')) return '#6B7280';
    if (val === 2 || val === 5 || title.includes('COMPLETED') || title.includes('CLOSED') || title.includes('CANCELLED')) return '#374151';
    return '#3B82F6';
  }

  private eventsPerPage(): number {
    return this.viewMode === 'map' ? AllEventsComponent.MAP_PAGE_SIZE : this.pageSize;
  }

  get_all_events(
    event_type: any,
    event_status?: any,
    search?: any,
    isLoadMore: boolean = false,
    keepVisible: boolean = false,
  ) {
    const request = ++this.listRequest;
    // Reset pagination if it's a new search/filter. A viewport refresh keeps
    // the pins on screen until the bounded response arrives.
    if (!isLoadMore) {
      this.currentPage = 1;
      this.hasMoreData = true;
      if (!keepVisible) {
        this.setEventsList([]);
      }
    }

    // Update current filters
    this.currentEventType = event_type || '';
    this.currentEventStatus = this.toFilterParam(event_status);
    this.currentSearch = search || '';

    this.isLoading = true;
    const perPage = this.eventsPerPage();

    this.commonServiceService
      .get_events(
        '',
        this.currentEventStatus,
        event_type,
        search,
        this.currentPage,
        perPage,
        this.toEventLevelParam(),
        this.appliedStartDate,
        this.appliedEndDate,
        this.toFilterParam(this.selectedSeason),
        null,
        true,
        'relevance',
        null,
        this.user_role_id,
        // Cards, list rows and map pins all read the venue from `event_venues`.
        true,
        this.viewMode === 'map' ? this.mapViewport : null,
      )
      .subscribe({
        next: (result: any) => {
          if (request !== this.listRequest) {
            return;
          }
          this.error_message = '';

          // A zero-result search/filter combination can come back as a bare 204 No
          // Content (confirmed live against the QA backend, e.g. `search=parent
          // abb&season_id=115`) rather than 200 with `data: []` — the service layer
          // (`get_events`'s `response.body?.data`) correctly passes that through as
          // `undefined` rather than throwing, but nothing downstream normalized it
          // back to an empty list. Left as `undefined`, `events_list?.length == 0`
          // (the empty-state condition, `!ngIf` in the template) evaluates to
          // `false` — `undefined == 0` is `false` in JS, unlike `?? []` — so
          // neither the card grid nor the empty-state illustration ever rendered,
          // just a permanently blank section.
          const events: Template1EventItem[] = result?.data ?? [];
          if (isLoadMore) {
            // Append new events to existing list
            this.setEventsList([...this.events_list, ...events]);
          } else {
            // Replace events list for new search/filter
            this.setEventsList(events);
          }

          this.total_events = this.events_list?.length;

          // Check if there's more data
          if (events.length < perPage) {
            this.hasMoreData = false;
          } else {
            this.hasMoreData = true;
          }

          // this.onSeasonChange(this.selectedSeason);
          this.isLoading = false;
        },
        error: (error: any) => {
          if (request !== this.listRequest) {
            return;
          }
          // Only clear events list if it's not a load more operation
          if (!isLoadMore && !keepVisible) {
            this.setEventsList([]);
            this.hasMoreData = false;
            this.error_message =
              error?.error?.msg || 'An error occurred while fetching events';
          } else if (isLoadMore) {
            // Preserve existing data but stop trying to load more.
            this.hasMoreData = false;
          }

          this.isLoading = false;
          console.error('Error fetching events:', error);
        },
        complete: () => {
          if (request !== this.listRequest) {
            return;
          }
          if (!keepVisible) {
            this.get_sections(this.currentEventType);
          }
          this.isLoading = false;
        },
      });
  }
  get_broadcasts(
    event_type: string = '',
    isLoadMore: boolean = false,
    searchTerm: any = null,
    event_status: any = null,
  ) {
    // Reset pagination if it's a new search/filter
    if (!isLoadMore) {
      this.currentPage = 1;
      this.setEventsList([]);
      this.hasMoreData = true;
    }

    // Update current filters
    this.currentEventType = event_type || '';

    this.isLoading = true;

    this.commonServiceService
      .get_broadcasts(
        event_type,
        searchTerm,
        this.appliedStartDate,
        this.appliedEndDate,
        this.toFilterParam(event_status),
      )
      .subscribe({
        next: (result: any) => {
          this.error_message = '';

          // Same zero-result-comes-back-as-204 gap as `get_all_events` above — a bare
          // `result.data.events.map(...)` on a `204 No Content` response (`data`
          // itself `undefined`, not `{ events: [] }`) throws, which — being
          // uncaught inside `next`, not routed to `error` — leaves `isLoading`
          // stuck `true` forever (the line resetting it never runs), so neither
          // the card grid, the empty-state illustration, nor the loading skeleton
          // (all gated on `!isLoading`/`isLoading`) can ever show again.
          const rawEvents: any[] = result?.data?.events ?? [];
          const normalizedEvents: Template1EventItem[] = rawEvents.map((event: any) =>
            this.normalizeEventData(event, 'get_broadcast'),
          );
          if (isLoadMore) {
            // Append new events to existing list
            this.setEventsList([...this.events_list, ...normalizedEvents]);
          } else {
            // Replace events list for new search/filter
            this.setEventsList(normalizedEvents);
          }

          this.total_events = this.events_list?.length;

          // Check if there's more data (for now assuming no pagination from backend)
          // You may need to adjust this based on the actual broadcast API response structure
          if (rawEvents.length < this.pageSize) {
            this.hasMoreData = false;
          } else {
            this.hasMoreData = false; // Set to false for now since broadcast API might not support pagination
          }

          this.isLoading = false;
        },
        error: (error: any) => {
          // Only clear events list if it's not a load more operation
          if (!isLoadMore) {
            this.setEventsList([]);
            this.hasMoreData = false;
            this.error_message = error.error.msg;
          } else {
            // For load more errors, preserve existing data but stop trying to load more
            this.hasMoreData = false;
          }

          this.isLoading = false;
        },
        complete: () => {
          this.isLoading = false;
        },
      });
  }

  // Add this method to normalize the data from both sources
  normalizeEventData(eventData: any, source: string): any {
    if (source === 'get_all_events') {
      return {
        id: eventData.id,
        name: eventData.name,
        event_description: eventData.event_description,
        event_start_date: eventData.event_start_date,
        event_end_date: eventData.event_end_date,
        banner: eventData.banner,
        logo: eventData.logo,
        event_type: eventData.event_type,
        published: eventData.published,
        source: 'tournament',
      };
    } else if (source === 'get_broadcast') {
      return {
        id: eventData.id,
        name: eventData.event_name, // Map event_name to name
        event_description: eventData?.event_description,
        event_start_date: eventData.event_start_date,
        event_end_date: eventData.event_end_date,
        banner: eventData.event_banner, // Map event_banner to banner
        thumbnail: eventData.event_thumbnail,
        logo: null, // Not available in broadcast data
        event_type: eventData.event_type,
        // Numeric event_status id, same table/values as get_all_events' event_status —
        // just returned under this key name for broadcast items.
        status: eventData.status,
        published: eventData.is_published, // Map is_published to published
        matches: eventData.matches, // Additional data for broadcast events
        venue_name: eventData.venue_name,
        source_event_id: eventData.source_event_id,
        source: 'broadcast',
      };
    }
    return eventData;
  }

  loadMoreEvents() {
    if (this.isLoading || !this.hasMoreData) return;

    // Check if we're on broadcasts route
    const currentRoute = window.location.href.split('/')[3];
    const isBroadcastRoute = currentRoute === 'broadcasts';

    this.currentPage++;

    if (isBroadcastRoute) {
      this.get_broadcasts(
        this.currentEventType,
        true, // isLoadMore = true
        this.searchQuery?.trim() || null,
        this.selectedEventStatus || null,
      );
    } else {
      this.get_all_events(
        this.currentEventType,
        this.currentEventStatus,
        this.currentSearch,
        true, // isLoadMore = true
      );
    }
  }

  tab_change(index: any) {
    this.selected_tab = index;
    this.event_tab_index.emit(index);
    this.event_statuses.forEach((status: any) => {
      status.checked = false;
    });

    // Keep filters when switching tabs, except the date range which is cleared
    this.appliedStartDate = '';
    this.appliedEndDate = '';
    this.dates = [];
    this.rangeStartDate = null;
    this.saveFilters();

    if (this.isBroadcastRoute) {
      // Handle broadcast tabs - pass current filters
      this.get_broadcasts(
        this.tabs_broadcasts.filter((x: any) => x.id == index)[0].event_type,
        false,
        this.searchQuery?.trim() || null,
        this.selectedEventStatus || null,
      );
    } else {
      // Handle regular event tabs - pass current filters
      this.get_all_events(
        this.tabs.filter((x: any) => x.id == index)[0].event_type,
        this.selectedEventStatus || '',
        this.searchQuery || '',
      );
    }
  }

  go_to_event(data: any) {
    // if (this.isPlayer) {
    //   // Player goes to view-event screen for self registration
    //   this.router.navigate(['/events/view-event', data.id]);
    // } else {
    // Non-player uses original logic
    // Store source_event_id in session storage if available
    if (data?.source_event_id) {
      sessionStorage.setItem(
        'source_event_id',
        data.source_event_id.toString(),
      );
    } else {
      // Clear session storage if no source_event_id (for regular events)
      sessionStorage.removeItem('source_event_id');
    }
    localStorage.setItem('event_type', data.event_type);
    if (this.is_sub_license) {
      this.subLicenseManagementService.setSubLicenseReturnContext({
        user_role_id: this.user_role_id,
        tab_index: 1,
        event_tab_index: this.selected_tab ?? 1,
      });
    }
    const currentRoute = this.is_sub_license
      ? 'events'
      : window.location.href.split('/')[3];
    const landingTabId = resolveEventLandingTabId(data.event_status, {
      tenantName: environment.tenant_Name,
      eventType: data.event_type,
    });
    this.router.navigate([
      currentRoute,
      ...buildEventDetailRouteSegments(data.id, landingTabId),
    ]);
    // }
  }
  onViewRegisterCount(data: any) {
    this.router.navigate(['/events/view-event', data.id]);
  }

  onSearchChange(data: any) {
    // Ensure data is properly handled (null, undefined, or string)
    const searchTerm = data || '';
    this.searchQuery = searchTerm;

    // Clear any existing timeout
    clearTimeout(this.searchTimeout);

    // Emit search term to debounced subject
    this.searchSubject.next(searchTerm);
  }

  onSearchBarInputExpanded(expanded: boolean): void {
    this.searchBarExpanded = expanded;
    this.cdr.detectChanges();
  }

  onSearchSubmit(data: any) {
    // Ensure data is properly handled (null, undefined, or string)
    const searchTerm = data || '';
    this.searchQuery = searchTerm;

    // Clear any pending search timeout
    clearTimeout(this.searchTimeout);

    // For immediate search submission, bypass debouncing
    this.performSearch(searchTerm);
  }

  isCheckboxChecked(checkboxId: number): boolean {
    return this.selectedStatus === checkboxId;
  }

  onStatusChange(event: any, data: any) {
    // Check if we're on broadcasts route
    const currentRoute = window.location.href.split('/')[3];
    const isBroadcastRoute = currentRoute === 'broadcasts';

    if (event.checked) {
      if (isBroadcastRoute) {
        // For broadcasts, we might need to implement status filtering in get_broadcasts
        // For now, call get_broadcasts with current tab's event_type
        this.get_broadcasts(
          this.tabs_broadcasts.filter((x: any) => x.id == this.selected_tab)[0]
            .event_type,
        );
      } else {
        this.get_all_events(this.currentEventType, data, this.currentSearch);
      }

      // Deselect all other checkboxes
      this.event_statuses.forEach((status: any) => {
        if (status.value !== data) {
          status.checked = false;
        }
      });
    } else {
      if (isBroadcastRoute) {
        this.get_broadcasts(
          this.tabs_broadcasts.filter((x: any) => x.id == this.selected_tab)[0]
            .event_type,
        );
      } else {
        this.get_all_events(this.currentEventType, '', this.currentSearch);
      }
    }
  }

  setViewMode(mode: 'grid' | 'list' | 'map'): void {
    const leavingMap = this.viewMode === 'map' && mode !== 'map';
    this.viewMode = mode;
    if (!leavingMap) {
      return;
    }
    this.mapViewport = null;
    this.get_all_events(
      this.currentEventType,
      this.currentEventStatus,
      this.currentSearch,
    );
  }

  /** Refetch events inside the visible map. Zooming out widens this box. */
  onMapViewport(bounds: MapBounds): void {
    if (this.viewMode !== 'map') {
      return;
    }
    this.mapViewport = bounds;
    this.get_all_events(
      this.currentEventType,
      this.currentEventStatus,
      this.currentSearch,
      false,
      true,
    );
  }

  change_grid() {
    this.setViewMode(this.viewMode === 'grid' ? 'list' : 'grid');
  }

  /**
   * Selects a view mode outright instead of toggling.
   *
   * The template-1 chrome shows grid and list as two separate buttons, so
   * clicking the already-active one must be a no-op rather than a flip.
   *
   * @param useGrid True for the poster grid, false for the compact list.
   */
  setGridView(useGrid: boolean): void {
    this.setViewMode(useGrid ? 'grid' : 'list');
  }

  get_sections(event_type: any) {
    this.isLoading = true;
    this.commonServiceService.get_sections('', event_type).subscribe({
      next: (result: any) => {
        try {
          const sections = result?.data ?? [];

          // Find ALL sections with name "Event Details"
          const eventDetailsSections = sections.filter(
            (section: any) => section.name === 'Event Details',
          );

          if (eventDetailsSections.length > 0) {
            let allEventLevelOptions: any[] = [];
            let allEventStatusOptions: any[] = [];

            // Collect dropdown options from all Event Details sections
            eventDetailsSections.forEach((section: any) => {
              if (Array.isArray(section.field_json)) {
                // Find the field with field_title "Event Level"
                const eventLevelField = section.field_json.find(
                  (field: any) => field.field_title === 'Event Level',
                );

                if (eventLevelField && Array.isArray(eventLevelField.dropdown_options)) {
                  allEventLevelOptions = allEventLevelOptions.concat(eventLevelField.dropdown_options);
                }
                const eventStatusField = section.field_json.find(
                  (field: any) => field.field_title === "Event Status"
                );

                if (eventStatusField && Array.isArray(eventStatusField.dropdown_options)) {
                  allEventStatusOptions = allEventStatusOptions.concat(eventStatusField.dropdown_options);
                }
              }
            });

            // Process Event Level options
            if (allEventLevelOptions.length > 0) {
              // Remove duplicates based on 'title' property (since values can be the same)
              const uniqueOptionsMap = new Map();
              allEventLevelOptions.forEach((option: any) => {
                if (!uniqueOptionsMap.has(option.title)) {
                  uniqueOptionsMap.set(option.title, option);
                }
              });

              // Map to eventLevelOptions format
              this.eventLevelOptions = Array.from(
                uniqueOptionsMap.values(),
              ).map((option: any) => ({
                value: option.value,
                label: option.title,
              }));
              this.selectedEventLevel = this.selectedEventLevel.map((item) =>
                this.resolveEventLevelLabel(item),
              );
              this.pendingEventLevel = [...this.selectedEventLevel];


            } else {
              console.warn(' No dropdown options found in Event Level fields');
            }

            // Process Event Status options
            if (allEventStatusOptions.length > 0) {
              // Remove duplicates based on 'title' property
              const uniqueStatusOptionsMap = new Map();
              allEventStatusOptions.forEach((option: any) => {
                if (!uniqueStatusOptionsMap.has(option.title)) {
                  uniqueStatusOptionsMap.set(option.title, option);
                }
              });

              // Map to eventStatusOptions format (only id and description)
              this.eventStatusOptions = Array.from(uniqueStatusOptionsMap.values()).map((option: any) => ({
                status_id: option.value,
                status_description: option.title
              }));


            }

            this.cdr.detectChanges();
          } else {
            console.warn(' Event Details section not found');
          }
        } catch (error) {
          console.error(' Error parsing get_sections response:', error);
        }
      },
      error: (error) => { },
      complete: () => {
        this.isLoading = false;
      },
    });
  }

  // Filter dropdown data
  seasonOptions = [
    { value: '2024-25', label: '2024-25' },
    { value: '2023-24', label: '2023-24' },
    { value: '2022-23', label: '2022-23' },
    { value: '2021-22', label: '2021-22' },
  ];
  eventLevelOptions = [
    { value: 'international', label: 'International' },
    { value: 'national', label: 'National' },
    { value: 'state', label: 'State' },
    { value: 'district', label: 'District' },
    { value: 'global', label: 'Global' },
  ];

  eventStatusOptions: any[] = [];

  // Selected filter values
  selectedSeason: string[] = [];
  selectedEventLevel: string[] = [];
  selectedEventStatus: string[] = [];
  pendingSeason: string[] = [];
  pendingEventLevel: string[] = [];
  pendingEventStatus: string[] = [];
  mobileFiltersOpen = false;
  pendingNearMe = false;
  private mobileFiltersApplying = false;

  // Search/filter values for dropdowns
  seasonSearch: string = '';
  eventLevelSearch: string = '';
  eventStatusSearch: string = '';
  date: Date[] | undefined;

  // Calendar properties
  weekDays = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun']; // Updated from locale on init/lang change
  selectedYear: number = new Date().getFullYear();
  // Jump-to-year range for the date-range overlay's compact year select
  readonly yearRange: number[] = Array.from({ length: 11 }, (_, i) => 2020 + i);
  dates: any = [];

  // Two-month view properties — default window is previous + current month
  currentMonthIndex: number = new Date().getMonth(); // Starting month index (0-11)
  visibleMonths: any = []; // Array to hold the 2 currently visible months
  rangeStartDate: string | null = null; // Track first selected date for range selection

  readonly datePresets: ReadonlyArray<{ id: DiscoveryDatePreset; labelKey: string }> = [
    { id: 'any', labelKey: 'any_time' },
    { id: 'lastWeek', labelKey: 'last_week' },
    { id: 'thisWeek', labelKey: 'this_week' },
    { id: 'nextWeek', labelKey: 'next_week' },
  ];

  // Applied date range (only set when user clicks Apply in calendar)
  appliedStartDate: string = '';
  appliedEndDate: string = '';

  // Category levels data
  level_list: any = [];

  // Seasons data
  seasons_list: any = [];
  onSeasonPanelToggle(opened: boolean): void {
    this.pendingSeason = [...this.selectedSeason];
    if (opened) {
      this.seasonSearch = '';
    }
  }

  applySeasonFilter(): void {
    this.selectedSeason = [...this.pendingSeason];
    this.saveFilters();
    this.seasonSelect.close();
    this.get_all_events(
      this.currentEventType,
      this.selectedEventStatus,
      this.currentSearch,
      false,
    );
  }

  /**
   * Compares a filter select's value against an option's.
   *
   * `EventsFilterStateService` stringifies every filter on its way into session
   * storage, so a restored status or season comes back as `'13'` while the
   * option still carries the API's `13`. MatSelect's default strict check would
   * leave that row unticked even though the trigger shows its name (the
   * trigger's own lookup is loose), so the two are compared as strings here.
   *
   * An arrow property, not a method: `compareWith` is invoked unbound. Also
   * used as the multi-select `compareWith` for template-1's own quick filters
   * (Angular Material calls it per-option either way).
   *
   * @param a select value
   * @param b option value
   * @returns True when the two name the same filter value.
   */
  readonly compareFilterValue = (
    a: string | number | null | undefined,
    b: string | number | null | undefined,
  ): boolean => String(a ?? '') === String(b ?? '');

  // Template-1's own quick filters apply immediately on selectionChange (no pending/Apply
  // step) — the mat-select is `multiple`, so it natively stays open between picks; no
  // manual `.close()` here or it would dismiss after the very first option.
  onSeasonChange(seasonId: any) {
    this.selectedSeason = seasonId;
    this.pendingSeason = [...this.selectedSeason];
    this.saveFilters();
    this.get_all_events(
      this.currentEventType,
      this.selectedEventStatus,
      this.currentSearch,
      false,
    );
  }

  onEventLevelChange(eventLevel: any) {
    this.selectedEventLevel = eventLevel;
    this.pendingEventLevel = [...this.selectedEventLevel];
    this.saveFilters();
    this.get_all_events(
      this.currentEventType,
      this.selectedEventStatus,
      this.currentSearch,
      false,
    );
  }

  onEventStatusChange(eventStatus: any) {
    this.selectedEventStatus = eventStatus;
    this.pendingEventStatus = [...this.selectedEventStatus];
    this.saveFilters();
    if (this.isBroadcastRoute) {
      this.get_broadcasts(this.currentEventType, false, null, eventStatus);
    } else {
      this.get_all_events(
        this.currentEventType,
        eventStatus,
        this.currentSearch,
        false,
      );
    }
  }

  // Classic UI: dropdown opens into a "pending" scratch copy; nothing is applied
  // until the Apply button below calls the matching apply*Filter method.
  onEventLevelPanelToggle(opened: boolean): void {
    this.pendingEventLevel = [...this.selectedEventLevel];
    if (opened) {
      this.eventLevelSearch = '';
    }
  }

  onEventStatusPanelToggle(opened: boolean): void {
    this.pendingEventStatus = [...this.selectedEventStatus];
    if (opened) {
      this.eventStatusSearch = '';
    }
  }

  applyEventLevelFilter(): void {
    this.selectedEventLevel = [...this.pendingEventLevel];
    this.saveFilters();
    this.eventLevelSelect.close();
    this.get_all_events(
      this.currentEventType,
      this.selectedEventStatus,
      this.currentSearch,
      false,
    );
  }

  applyEventStatusFilter(): void {
    this.selectedEventStatus = [...this.pendingEventStatus];
    this.saveFilters();
    this.eventStatusSelect.close();
    if (this.isBroadcastRoute) {
      this.get_broadcasts(
        this.currentEventType,
        false,
        null,
        this.selectedEventStatus,
      );
    } else {
      this.get_all_events(
        this.currentEventType,
        this.selectedEventStatus,
        this.currentSearch,
        false,
      );
    }
  }

  // ViewChild references for mat-select dropdowns
  @ViewChild('seasonSelect', { static: false }) seasonSelect!: MatSelect;
  @ViewChild('eventLevelSelect', { static: false })
  eventLevelSelect!: MatSelect;
  @ViewChild('eventStatusSelect', { static: false })
  eventStatusSelect!: MatSelect;
  @ViewChild('dateRangeOverlay') dateRangeOverlay!: OverlayPanel;

  /**
   * Opens the season dropdown.
   *
   * @param select Trigger's own select — see `toggleEventLevelFilter`.
   */
  toggleSeasonFilter(select?: MatSelect) {
    setTimeout(() => (select ?? this.seasonSelect)?.open(), 0);
  }

  /**
   * Opens the event-level dropdown.
   *
   * @param select Trigger's own select. Passed by the template so the phone
   *   drawer (an embedded view) does not have to rely on the ViewChild.
   */
  toggleEventLevelFilter(select?: MatSelect) {
    setTimeout(() => (select ?? this.eventLevelSelect)?.open(), 0);
  }

  /**
   * Opens the event-status dropdown.
   *
   * @param select Trigger's own select — see `toggleEventLevelFilter`.
   */
  toggleEventStatusFilter(select?: MatSelect) {
    setTimeout(() => (select ?? this.eventStatusSelect)?.open(), 0);
  }

  // Build a single month's grid (Monday-start), flagging range membership +
  // pill-edge rounding so the template can render a contiguous range bar.
  private buildMonth(year: number, monthIndex: number) {
    const monthNames = this.getLocalizedMonthNames();
    const daysInMonth = new Date(year, monthIndex + 1, 0).getDate();
    const firstDayOfMonth = new Date(year, monthIndex, 1).getDay();
    const startDay = firstDayOfMonth === 0 ? 6 : firstDayOfMonth - 1; // Adjust for Monday-start

    const sortedDates = this.dates?.length ? [...this.dates].sort() : [];
    const rangeStartStr = sortedDates[0];
    const rangeEndStr = sortedDates[sortedDates.length - 1];

    const days: any[] = Array(startDay).fill(null);

    for (let j = 1; j <= daysInMonth; j++) {
      const dateStr = `${year}-${(monthIndex + 1).toString().padStart(2, '0')}-${j.toString().padStart(2, '0')}`;
      const inRange = this.dates?.includes(dateStr) ?? false;

      days.push({
        date: j,
        dateStr,
        selected: inRange,
        inRange,
        isRangeStart: inRange && dateStr === rangeStartStr,
        isRangeEnd: inRange && dateStr === rangeEndStr,
        roundLeft: false,
        roundRight: false,
        disabled: false,
      });
    }

    // Second pass: a range-day rounds on the side that isn't touching
    // another range-day in the same row, so contiguous days read as one pill.
    for (let i = 0; i < days.length; i++) {
      const day = days[i];
      if (!day || !day.inRange) continue;
      const col = i % 7;
      const prev = days[i - 1];
      const next = days[i + 1];
      day.roundLeft = col === 0 || !prev || !prev.inRange;
      day.roundRight = col === 6 || !next || !next.inRange;
    }

    return { name: monthNames[monthIndex], monthIndex, year, days };
  }

  private getLocaleForLanguage(): string {
    const langMap: Record<string, string> = {
      en: 'en-US',
      de: 'de-DE',
      fr: 'fr-FR',
      it: 'it-IT',
      spain: 'es-ES',
      norway: 'no-NO',
      swedan: 'sv-SE',
      sv: 'sl-SI',
      cs: 'cs-CZ',
      da: 'da-DK',
    };

    const currentLang =
      this.translateService.currentLang || this.translateService.defaultLang || 'en';
    return langMap[currentLang] || 'en-US';
  }

  private getLocalizedMonthNames(): string[] {
    const locale = this.getLocaleForLanguage();
    return Array.from({ length: 12 }, (_, monthIndex) =>
      new Intl.DateTimeFormat(locale, { month: 'long' }).format(
        new Date(2000, monthIndex, 1),
      ),
    );
  }

  private getLocalizedWeekDays(): string[] {
    const locale = this.getLocaleForLanguage();
    const mondayBaseDate = new Date(Date.UTC(2021, 10, 1));

    return Array.from({ length: 7 }, (_, dayIndex) => {
      const date = new Date(mondayBaseDate);
      date.setUTCDate(mondayBaseDate.getUTCDate() + dayIndex);
      return new Intl.DateTimeFormat(locale, { weekday: 'short' }).format(date);
    });
  }

  private getVisibleMonthCount(): number {
    if (this.mobileFiltersOpen) {
      return 1;
    }
    return window.innerWidth <= 768 ? 1 : 2;
  }

  // Desktop shows two months, so anchor one month back (August -> July | August).
  // Mobile anchors on the current month so "today" stays on screen.
  private resetVisibleMonthsToCurrentDate(): void {
    const now = new Date();
    let monthIndex = now.getMonth();

    if (this.getVisibleMonthCount() === 2 && monthIndex > 0) {
      monthIndex -= 1;
    }

    this.currentMonthIndex = monthIndex;
    this.selectedYear = now.getFullYear();
  }

  // Build the currently visible month pair (1 on mobile, 2 on desktop) from
  // the anchor year/month, generating each on demand so navigation can cross
  // year boundaries without a separate year control.
  updateVisibleMonths() {
    const monthsToShow = this.getVisibleMonthCount();
    this.selectedYear = this.normalizeSelectedYear();

    if (
      this.currentMonthIndex === undefined ||
      this.currentMonthIndex === null
    ) {
      this.resetVisibleMonthsToCurrentDate();
    }

    this.visibleMonths = [];
    for (let i = 0; i < monthsToShow; i++) {
      let monthIndex = this.currentMonthIndex + i;
      let year = this.selectedYear;
      if (monthIndex > 11) {
        monthIndex -= 12;
        year += 1;
      }
      this.visibleMonths.push(this.buildMonth(year, monthIndex));
    }
  }

  // Navigate to the next month (anchor advances 1 month, rolling into the next year)
  nextMonths() {
    this.currentMonthIndex += 1;
    if (this.currentMonthIndex > 11) {
      this.currentMonthIndex = 0;
      this.selectedYear = this.normalizeSelectedYear() + 1;
    }
    this.updateVisibleMonths();
  }

  // Navigate to the previous month (anchor recedes 1 month, rolling into the previous year)
  prevMonths() {
    this.currentMonthIndex -= 1;
    if (this.currentMonthIndex < 0) {
      this.currentMonthIndex = 11;
      this.selectedYear = this.normalizeSelectedYear() - 1;
    }
    this.updateVisibleMonths();
  }

  // Jump straight to a chosen year (compact year select in the overlay header)
  onYearJump() {
    this.selectedYear = this.normalizeSelectedYear();
    this.currentMonthIndex = 0;
    this.updateVisibleMonths();
  }

  /** Native `<select>` ngModel can still yield strings — keep calendar math numeric. */
  private normalizeSelectedYear(): number {
    const year = Number(this.selectedYear);
    return Number.isFinite(year) ? year : new Date().getFullYear();
  }

  // Toggle date selection with range functionality
  toggleDateSelection(dateStr: string) {
    if (!this.dates) {
      this.dates = [];
    }

    const index = this.dates.indexOf(dateStr);

    if (index > -1) {
      // If already selected, deselect and clear range
      this.dates = [];
      this.rangeStartDate = null;
    } else {
      // Handle range selection
      if (this.dates.length === 0) {
        // First date selection - just select it and mark as range start
        this.dates.push(dateStr);
        this.rangeStartDate = dateStr;
      } else if (this.dates.length === 1 && this.rangeStartDate) {
        // Second date selection - create range
        const startDate = new Date(this.rangeStartDate);
        const endDate = new Date(dateStr);

        // Clear current selection
        this.dates = [];

        // Ensure start date is before end date
        const rangeStart = startDate < endDate ? startDate : endDate;
        const rangeEnd = startDate < endDate ? endDate : startDate;

        // Add all dates in range
        const currentDate = new Date(rangeStart);
        while (currentDate <= rangeEnd) {
          const dateString = `${currentDate.getFullYear()}-${(
            currentDate.getMonth() + 1
          )
            .toString()
            .padStart(2, '0')}-${currentDate
              .getDate()
              .toString()
              .padStart(2, '0')}`;
          this.dates.push(dateString);
          currentDate.setDate(currentDate.getDate() + 1);
        }
        this.rangeStartDate = null; // Clear range start after creating range
      } else {
        // Multiple dates selected or unexpected state, start fresh
        this.dates = [dateStr];
        this.rangeStartDate = dateStr;
      }
    }

    // Regenerate calendar to update selected states
    this.updateVisibleMonths();

    // Update the calendar display
    this.cdr.detectChanges();
  }

  // Open the date-range overlay, anchored on the applied range's start month if one exists.
  openCalendar(event: Event) {
    if (this.appliedStartDate && this.appliedEndDate) {
      this.dates = [];
      const startDate = new Date(this.appliedStartDate);
      const endDate = new Date(this.appliedEndDate);

      const currentDate = new Date(startDate);
      while (currentDate <= endDate) {
        const dateString = `${currentDate.getFullYear()}-${(
          currentDate.getMonth() + 1
        )
          .toString()
          .padStart(2, '0')}-${currentDate
            .getDate()
            .toString()
            .padStart(2, '0')}`;
        this.dates.push(dateString);
        currentDate.setDate(currentDate.getDate() + 1);
      }

      this.selectedYear = startDate.getFullYear();
      this.currentMonthIndex = startDate.getMonth();
    } else {
      this.resetVisibleMonthsToCurrentDate();
    }

    this.updateVisibleMonths();
    this.cdr.detectChanges();
    this.dateRangeOverlay.toggle(event);
  }

  // Apply selected dates
  applyDates() {
    // Get date range for API call
    let startDate = '';
    let endDate = '';

    if (this.dates && this.dates.length > 0) {
      // Sort dates to ensure correct order
      const sortedDates = this.dates.sort();
      startDate = sortedDates[0]; // First date
      endDate = sortedDates[sortedDates.length - 1]; // Last date

      // Store applied dates
      this.appliedStartDate = startDate;
      this.appliedEndDate = endDate;
    } else {
      // Clear applied dates if no dates selected
      this.appliedStartDate = '';
      this.appliedEndDate = '';
    }

    // Call API with date range - the dates are passed via appliedStartDate and appliedEndDate
    // which are used in the get_all_events method\
    if (this.isBroadcastRoute) {
      this.get_broadcasts(
        this.currentEventType,
        false,
        null,
        this.selectedEventStatus,
      );
    } else {
      this.get_all_events(
        this.currentEventType,
        this.selectedEventStatus,
        this.currentSearch,
        false, // Reset pagination for new filter
      );
    }

    this.rangeStartDate = null; // Clear range start when applying dates
    this.saveFilters();
    this.dateRangeOverlay.hide();
  }

  // Close calendar without applying dates
  closeCalendar() {
    this.fillDatesFromApplied();
    this.rangeStartDate = null;
    this.dateRangeOverlay.hide();
  }

  // Clear the applied date filter
  clearDateFilter(event: Event) {
    event.stopPropagation(); // Prevent opening calendar
    this.appliedStartDate = '';
    this.appliedEndDate = '';
    this.dates = []; // Also clear any selected dates in calendar
    this.rangeStartDate = null;
    this.saveFilters();

    // Trigger API call without date filters
    if (this.isBroadcastRoute) {
      this.get_broadcasts(
        this.currentEventType,
        false,
        null,
        this.selectedEventStatus,
      );
    } else {
      this.get_all_events(
        this.currentEventType,
        this.selectedEventStatus,
        this.currentSearch,
        false, // Reset pagination
      );
    }
  }

  // Get selected season name for display
  getSelectedSeasonName(): string {
    if (!this.selectedSeason.length || !this.seasons_list) return '';
    const names = this.selectedSeason
      .map(
        (id) =>
          this.seasons_list.find((season: any) => String(season.id) === String(id))
            ?.season_name,
      )
      .filter((name): name is string => !!name);
    if (names.length === 0) return '';
    if (names.length === 1) return names[0];
    return `${names[0]} +${names.length - 1}`;
  }

  // Get selected event level name for display
  getSelectedEventLevelName(): string {
    if (!this.selectedEventLevel.length) return '';
    const names = this.selectedEventLevel
      .map((item) => this.resolveEventLevelLabel(item))
      .filter((label) => !!label);
    if (names.length === 0) return '';
    if (names.length === 1) return names[0];
    return `${names[0]} +${names.length - 1}`;
  }

  // Get selected event status name for display
  getSelectedEventStatusName(): string {
    if (!this.selectedEventStatus.length || !this.eventStatusOptions) return '';
    const names = this.selectedEventStatus
      .map((id) => {
        const status = this.eventStatusOptions.find(
          (item) => String(item.status_id) === String(id),
        );
        return status
          ? this.translateService.instant(status.status_description)
          : '';
      })
      .filter((label) => !!label);
    if (names.length === 0) return '';
    if (names.length === 1) return names[0];
    return `${names[0]} +${names.length - 1}`;
  }

  // Clear season filter
  clearSeasonFilter(event: Event) {
    event.stopPropagation();
    this.selectedSeason = [];
    this.pendingSeason = [];
    this.saveFilters();
    this.get_all_events(
      this.currentEventType,
      this.selectedEventStatus,
      this.currentSearch,
      false,
    );
  }

  // Clear event level filter
  clearEventLevelFilter(event: Event) {
    event.stopPropagation();
    this.selectedEventLevel = [];
    this.pendingEventLevel = [];
    this.saveFilters();
    this.get_all_events(
      this.currentEventType,
      this.selectedEventStatus,
      this.currentSearch,
      false,
    );
  }

  // Clear event status filter
  clearEventStatusFilter(event: Event) {
    event.stopPropagation();
    this.selectedEventStatus = [];
    this.pendingEventStatus = [];
    this.saveFilters();
    if (this.isBroadcastRoute) {
      this.get_broadcasts(this.currentEventType, false, null, null);
    } else {
      this.get_all_events(
        this.currentEventType,
        '',
        this.currentSearch,
        false,
      );
    }
  }

  /**
   * Clears every active filter (status, season, level, date range) at once and refetches —
   * the phone filter drawer's own header button. The four `clear*Filter` methods above each
   * do this for one field and immediately refetch; calling all four here would fire four
   * redundant requests; this clears every field first and fetches exactly once.
   * @param event - The click event, stopped so it doesn't bubble into the drawer sheet
   */
  clearAllFilters(event: Event): void {
    event.stopPropagation();
    this.selectedEventStatus = [];
    this.pendingEventStatus = [];
    this.selectedSeason = [];
    this.pendingSeason = [];
    this.selectedEventLevel = [];
    this.pendingEventLevel = [];
    this.appliedStartDate = '';
    this.appliedEndDate = '';
    this.dates = [];
    this.rangeStartDate = null;
    this.saveFilters();

    if (this.isBroadcastRoute) {
      this.get_broadcasts(this.currentEventType, false, null, null);
    } else {
      this.get_all_events(this.currentEventType, [], this.currentSearch, false);
    }
  }

  // Filtered getters for dropdown options
  get filteredSeasons() {
    if (!this.seasons_list) return [];
    if (!this.seasonSearch.trim()) return this.seasons_list;
    return this.seasons_list.filter((season: any) =>
      season.season_name
        .toLowerCase()
        .includes(this.seasonSearch.toLowerCase()),
    );
  }

  get filteredEventLevels() {
    if (!this.eventLevelOptions) return [];
    if (!this.eventLevelSearch.trim()) return this.eventLevelOptions;
    return this.eventLevelOptions.filter((level: any) =>
      level.label.toLowerCase().includes(this.eventLevelSearch.toLowerCase()),
    );
  }

  get filteredEventStatuses() {
    if (!this.eventStatusOptions) return [];
    if (!this.eventStatusSearch.trim()) return this.eventStatusOptions;
    return this.eventStatusOptions.filter((status: any) =>
      status.status_description
        .toLowerCase()
        .includes(this.eventStatusSearch.toLowerCase()),
    );
  }

  _showLoader: boolean = false;
  // Fetch seasons data; onDone is called after success or error
  get_seasons(onDone?: () => void) {
    this._showLoader = true;
    this.pointLeaderboardService.getSeasons(100).subscribe({
      next: (result: any) => {
        this.seasons_list = [];
        this.seasons_list = result.seasons.filter(
          (season: any) => season.is_active,
        );
        // Preserve selectedSeason from filter state if valid; otherwise default to ongoing
        if (!this.isBroadcastRoute && this.seasons_list.length > 0) {
          const validFromSession = this.selectedSeason.filter((id) =>
            this.seasons_list.some((season: any) => String(season.id) === String(id)),
          );
          if (validFromSession.length > 0) {
            this.selectedSeason = validFromSession;
          } else {
            const ongoingSeasonId = this.seasons_list.find(
              (season: any) => season.status?.toLowerCase() === 'ongoing',
            )?.id;
            this.selectedSeason = ongoingSeasonId ? [String(ongoingSeasonId)] : [];
          }
          this.pendingSeason = [...this.selectedSeason];
        } else if (this.isBroadcastRoute) {
          this.selectedSeason = [];
          this.pendingSeason = [];
        }
        this.cdr.detectChanges();
      },
      error: (error: any) => {
        this._showLoader = false;
        this.isLoading = false;
      },
      complete: () => {
        this._showLoader = false;
        onDone?.();
      },
    });
  }

  // Get display text for date range button (uses APPLIED dates)
  getDateRangeText(): string {
    if (!this.appliedStartDate && !this.appliedEndDate) {
      return 'select_date';
    }

    const startDate = new Date(this.appliedStartDate);
    const endDate = new Date(this.appliedEndDate);

    // If only one date
    if (this.appliedStartDate === this.appliedEndDate) {
      return startDate.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
      });
    }

    const startMonth = startDate.getMonth();
    const endMonth = endDate.getMonth();
    const startYear = startDate.getFullYear();
    const endYear = endDate.getFullYear();

    const startDay = startDate.getDate();
    const endDay = endDate.getDate();

    // If same month and year
    if (startMonth === endMonth && startYear === endYear) {
      const monthName = startDate.toLocaleDateString('en-US', {
        month: 'long',
      });
      return `${startDay}-${endDay} ${monthName} ${startYear}`;
    } else if (startYear === endYear) {
      // Different months but same year: "23 March - 15 April 2023"
      const startMonthName = startDate.toLocaleDateString('en-US', {
        month: 'long',
      });
      const endMonthName = endDate.toLocaleDateString('en-US', {
        month: 'long',
      });

      return `${startDay} ${startMonthName} - ${endDay} ${endMonthName} ${startYear}`;
    } else {
      // Different years: "23 March 2020 - 15 April 2025"
      const startMonthName = startDate.toLocaleDateString('en-US', {
        month: 'long',
      });
      const endMonthName = endDate.toLocaleDateString('en-US', {
        month: 'long',
      });

      return `${startDay} ${startMonthName} ${startYear} - ${endDay} ${endMonthName} ${endYear}`;
    }
  }

  // Get display text for the date-range overlay footer, e.g.
  // "Mon, 19 May 2024 - Fri, 5 Jun 2024  (18 days)" (uses SELECTED dates)
  getSelectedDatesText(): string {
    if (!this.dates || this.dates.length === 0) {
      return '';
    }

    const sortedDates = [...this.dates].sort();
    const startDate = new Date(sortedDates[0]);
    const endDate = new Date(sortedDates[sortedDates.length - 1]);
    const locale = this.getLocaleForLanguage();
    const formatDate = (date: Date) =>
      new Intl.DateTimeFormat(locale, {
        weekday: 'short',
        day: 'numeric',
        month: 'short',
        year: 'numeric',
      }).format(date);

    if (this.dates.length === 1) {
      return formatDate(startDate);
    }

    return `${formatDate(startDate)} - ${formatDate(endDate)}  (${this.dates.length} days)`;
  }

  isPlayer: boolean = false;
  checkRole() {
    if (
      typeof window !== 'undefined' &&
      typeof window.localStorage !== 'undefined'
    ) {
      const token = localStorage.getItem('webToken') || localStorage.getItem('token');
      if (token) {
        try {
          const payload = JSON.parse(atob(token.split('.')[1]));
          this.isPlayer = payload?.role_name?.toLowerCase() === 'player';
        } catch {
          this.isPlayer = false;
        }
      }
    }
  }
  onNewRegister(event: any): void {
    if (event) {
      if (
        typeof window !== 'undefined' &&
        typeof window.sessionStorage !== 'undefined'
      ) {
        sessionStorage.setItem('selectedTournamentName', event.name ?? '');
        sessionStorage.setItem(
          'selectedTournamentDetails',
          JSON.stringify(event),
        );
        sessionStorage.setItem('event', JSON.stringify(event));
        sessionStorage.setItem('event_id', String(event.id ?? ''));
        sessionStorage.setItem('event_type', event.event_type ?? '');
        sessionStorage.setItem('delegation', event.delegation ?? '');
      }
      this.orderService.selectedTournamentName = event.name;
      this.selectedTournamentId = event.id;
      const eventId = event.id || 'vestland-2026';
      this.router.navigate(['new-registration', eventId]);
    }
  }

  onRegister(event: any) {
    // Check if user is logged in
    const token =
      typeof window !== 'undefined' ? localStorage.getItem('token') : null;
    if (!token) {
      this.commonServiceService.login_subject.next(true);
      this.commonServiceService.is_login_success.next(false);
      return;
    }

    // store event in the session storage
    sessionStorage.setItem('event', JSON.stringify(event));

    if (this.isPlayer) {
      // Player goes to self-registration screen
      this.router.navigate(['events', 'register', event.id]);
    } else {
      // Non-player goes to the registration page using navigateToMatchType logic
      if (event) {
        if (
          typeof window !== 'undefined' &&
          typeof window.sessionStorage !== 'undefined'
        ) {
          sessionStorage.setItem('selectedTournamentName', event.name);
          sessionStorage.setItem(
            'selectedTournamentDetails',
            JSON.stringify(event),
          );
          sessionStorage.setItem('event_id', event.id);
          sessionStorage.setItem('event_type', event.event_type);
          sessionStorage.setItem('delegation', event.delegation);
        }
        this.orderService.selectedTournamentName = event.name;
        this.selectedTournamentId = event.id;
        this.router.navigate([`registration/${event.id}`]);
      }
    }
  }

  getLocation(item: any) {
    return extractEventVenueLocation(item).address;
  }

  eventKm(item: unknown): number | null {
    if (!this.nearMeOrigin) {
      return null;
    }
    const km = eventDistanceKm(item, this.nearMeOrigin);
    return km == null ? null : Math.round(km);
  }

  toggleNearMe(): void {
    const enabled = !this.nearMeOrigin && !this.nearMePending;
    this.syncNearMe(enabled);
  }

  openMobileFilters(): void {
    this.pendingEventStatus = [...this.selectedEventStatus];
    this.pendingSeason = [...this.selectedSeason];
    this.pendingEventLevel = [...this.selectedEventLevel];
    this.pendingNearMe = !!this.nearMeOrigin;
    this.mobileFiltersOpen = true;
    this.showAppliedRangeOnCalendar();
  }

  onMobileFiltersClosed(): void {
    const applying = this.mobileFiltersApplying;
    this.mobileFiltersApplying = false;
    this.mobileFiltersOpen = false;
    if (applying) {
      return;
    }
    this.fillDatesFromApplied();
    this.rangeStartDate = null;
    this.updateVisibleMonths();
  }

  applyMobileFilters(): void {
    this.mobileFiltersApplying = true;
    this.selectedEventStatus = [...this.pendingEventStatus];
    this.selectedSeason = [...this.pendingSeason];
    this.selectedEventLevel = [...this.pendingEventLevel];
    this.commitPendingDates();
    this.saveFilters();
    const enableNearMe = this.pendingNearMe;
    const nearMeChanged = enableNearMe !== !!this.nearMeOrigin;
    this.mobileFiltersOpen = false;
    if (nearMeChanged) {
      this.syncNearMe(enableNearMe);
    }
    this.reloadFilteredEvents();
  }

  clearMobileFilters(): void {
    this.pendingEventStatus = [];
    this.pendingSeason = [];
    this.pendingEventLevel = [];
    this.selectedEventStatus = [];
    this.selectedSeason = [];
    this.selectedEventLevel = [];
    this.pendingNearMe = false;
    this.dates = [];
    this.appliedStartDate = '';
    this.appliedEndDate = '';
    this.rangeStartDate = null;
    if (this.nearMeOrigin || this.nearMePending) {
      this.syncNearMe(false);
    }
    this.saveFilters();
    this.resetVisibleMonthsToCurrentDate();
    this.updateVisibleMonths();
    this.reloadFilteredEvents();
  }

  appliedDiscoveryFilterCount(): number {
    let count = this.selectedEventStatus.length;
    if (!this.isBroadcastRoute) {
      count += this.selectedSeason.length + this.selectedEventLevel.length;
    }
    if (this.appliedStartDate || this.appliedEndDate) {
      count += 1;
    }
    if (this.nearMeOrigin) {
      count += 1;
    }
    return count;
  }

  toggleDiscoveryOption(kind: 'status' | 'season' | 'level', value: string): void {
    const current = this.pendingFor(kind);
    const next = current.includes(value)
      ? current.filter((item) => item !== value)
      : [...current, value];
    this.assignPending(kind, next);
  }

  private pendingFor(kind: 'status' | 'season' | 'level'): string[] {
    if (kind === 'status') {
      return this.pendingEventStatus;
    }
    if (kind === 'season') {
      return this.pendingSeason;
    }
    return this.pendingEventLevel;
  }

  private assignPending(kind: 'status' | 'season' | 'level', next: string[]): void {
    if (kind === 'status') {
      this.pendingEventStatus = next;
      return;
    }
    if (kind === 'season') {
      this.pendingSeason = next;
      return;
    }
    this.pendingEventLevel = next;
  }

  private showAppliedRangeOnCalendar(): void {
    if (this.appliedStartDate && this.appliedEndDate) {
      this.fillDatesFromApplied();
      const startDate = new Date(this.appliedStartDate);
      this.selectedYear = startDate.getFullYear();
      this.currentMonthIndex = startDate.getMonth();
    } else {
      this.resetVisibleMonthsToCurrentDate();
    }
    this.updateVisibleMonths();
  }

  private fillDatesFromApplied(): void {
    if (!(this.appliedStartDate && this.appliedEndDate)) {
      this.dates = [];
      return;
    }
    const dates: string[] = [];
    const startDate = new Date(this.appliedStartDate);
    const endDate = new Date(this.appliedEndDate);
    const currentDate = new Date(startDate);
    while (currentDate <= endDate) {
      dates.push(this.formatCalendarDay(currentDate));
      currentDate.setDate(currentDate.getDate() + 1);
    }
    this.dates = dates;
  }

  private formatCalendarDay(date: Date): string {
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const day = date.getDate().toString().padStart(2, '0');
    return `${date.getFullYear()}-${month}-${day}`;
  }

  activeDatePreset(): DiscoveryDatePreset | null {
    const selected = this.sortedSelectedDates();
    if (selected.length === 0) {
      return 'any';
    }
    const weeks: Array<Exclude<DiscoveryDatePreset, 'any'>> = ['lastWeek', 'thisWeek', 'nextWeek'];
    for (const id of weeks) {
      const days = this.daysInWeek(id);
      const sameLength = selected.length === days.length;
      const sameEnds = selected[0] === days[0] && selected[selected.length - 1] === days[days.length - 1];
      if (sameLength && sameEnds) {
        return id;
      }
    }
    return null;
  }

  applyDatePreset(id: DiscoveryDatePreset): void {
    this.rangeStartDate = null;
    if (id === 'any') {
      this.dates = [];
      this.resetVisibleMonthsToCurrentDate();
      this.updateVisibleMonths();
      this.cdr.detectChanges();
      return;
    }
    const days = this.daysInWeek(id);
    this.dates = days;
    const start = this.parseCalendarDay(days[0]);
    this.selectedYear = start.getFullYear();
    this.currentMonthIndex = start.getMonth();
    this.updateVisibleMonths();
    this.cdr.detectChanges();
  }

  private sortedSelectedDates(): string[] {
    if (!Array.isArray(this.dates)) {
      return [];
    }
    return this.dates.map((value) => String(value)).sort((a, b) => a.localeCompare(b));
  }

  private daysInWeek(id: Exclude<DiscoveryDatePreset, 'any'>): string[] {
    const start = this.mondayOf(new Date());
    if (id === 'lastWeek') {
      start.setDate(start.getDate() - 7);
    } else if (id === 'nextWeek') {
      start.setDate(start.getDate() + 7);
    }
    const end = new Date(start);
    end.setDate(start.getDate() + 6);
    const days: string[] = [];
    const cursor = new Date(start);
    while (cursor <= end) {
      days.push(this.formatCalendarDay(cursor));
      cursor.setDate(cursor.getDate() + 1);
    }
    return days;
  }

  private mondayOf(date: Date): Date {
    const start = new Date(date.getFullYear(), date.getMonth(), date.getDate());
    const weekday = start.getDay();
    const mondayOffset = weekday === 0 ? -6 : 1 - weekday;
    start.setDate(start.getDate() + mondayOffset);
    return start;
  }

  private parseCalendarDay(value: string): Date {
    const [yearText, monthText, dayText] = value.split('-');
    const year = Number(yearText);
    const month = Number(monthText);
    const day = Number(dayText);
    return new Date(
      Number.isFinite(year) ? year : new Date().getFullYear(),
      Number.isFinite(month) ? month - 1 : 0,
      Number.isFinite(day) ? day : 1,
    );
  }

  private commitPendingDates(): void {
    if (this.dates?.length) {
      const sortedDates = [...this.dates].sort((a: string, b: string) => a.localeCompare(b));
      this.appliedStartDate = sortedDates[0];
      this.appliedEndDate = sortedDates[sortedDates.length - 1];
    } else {
      this.appliedStartDate = '';
      this.appliedEndDate = '';
    }
    this.rangeStartDate = null;
  }

  private reloadFilteredEvents(): void {
    if (this.isBroadcastRoute) {
      this.get_broadcasts(this.currentEventType, false, null, this.selectedEventStatus);
      return;
    }
    this.get_all_events(
      this.currentEventType,
      this.selectedEventStatus,
      this.currentSearch,
      false,
    );
  }

  private syncNearMe(enabled: boolean): void {
    if (!enabled) {
      this.nearMeRequest += 1;
      this.nearMeOrigin = null;
      this.nearMePending = false;
      this.setEventsList(this.events_list);
      return;
    }
    if (this.nearMeOrigin) {
      return;
    }
    const geo = globalThis.navigator?.geolocation;
    if (!geo) {
      this.pendingNearMe = false;
      this.notifyLocationDenied();
      return;
    }
    this.nearMePending = true;
    const request = ++this.nearMeRequest;
    geo.getCurrentPosition(
      (position) => this.applyNearMe(request, {
        lat: position.coords.latitude,
        lng: position.coords.longitude,
      }),
      () => {
        this.pendingNearMe = false;
        this.applyNearMe(request, null);
        this.notifyLocationDenied();
      },
      { enableHighAccuracy: false, timeout: 8000, maximumAge: 300000 },
    );
  }

  private notifyLocationDenied(): void {
    const message = this.translateService.instant('location_unavailable');
    this.snackBar.open(message, undefined, { duration: 4000 });
  }

  private applyNearMe(request: number, origin: GeoPoint | null): void {
    if (request !== this.nearMeRequest) {
      return;
    }
    this.nearMePending = false;
    this.nearMeOrigin = origin;
    this.setEventsList(this.events_list);
  }

  private setEventsList(list: unknown[] | null | undefined): void {
    this.events_list = list ?? [];
    this.displayedEvents = this.nearMeOrigin
      ? sortEventsByDistance(list ?? [], this.nearMeOrigin)
      : (list ?? []);
  }

  /**
   * `getOrganiserDetails` is bound in the template ([organiserDetails]=
   * "getOrganiserDetails(item)"), so it runs on every change-detection pass
   * for every row. Caching by the `item` object's own identity means a CD
   * pass that re-renders the same (unchanged) item is a cheap WeakMap hit
   * instead of re-walking extra_json and allocating a new object; a genuine
   * refresh that creates new item objects just misses the cache and
   * recomputes, so it self-invalidates without any manual cache-clearing.
   */
  private readonly organiserDetailsCache = new WeakMap<object, OrganiserDetails>();

  /**
   * `getOrganiserDetails` is bound in the template — the parsing itself
   * lives in the shared `extractOrganiserDetails()` (models/event/organiser-details.ts,
   * also used by events-home's event-organizer-details section) so both
   * places read `extra_json` the same way; this just adds the identity cache.
   */
  getOrganiserDetails(item: any): OrganiserDetails {
    if (!item || typeof item !== 'object') {
      return EMPTY_ORGANISER_DETAILS;
    }
    const cached = this.organiserDetailsCache.get(item);
    if (cached) {
      return cached;
    }

    const details = extractOrganiserDetails(item);
    this.organiserDetailsCache.set(item, details);
    return details;
  }

  openLoginPopup() {

    this.commonServiceService.login_subject.next(true);
    this.commonServiceService.is_login_success.next(false);
  }

  trackByIndex(index: number, item: any): number {
    return index;
  }

  private _cachedDisplayedEvents: any[] | null = null;
  private _cachedCols: number = 0;
  private _cachedRows: any[][] = [];

  /** Cards per virtual-scroll row; measured from the grid viewport width (see gridViewport). */
  gridColumns = 1;
  /** Height of one virtual grid row: the 400px card + 2.5rem gap (matches the template's itemSize). */
  readonly gridRowHeight = 440;
  private gridResizeObserver?: ResizeObserver;
  private gridRangeSubscription?: Subscription;
  private readonly ngZone = inject(NgZone);

  /**
   * Keeps the virtualized grid's column count equal to what the old
   * `repeat(auto-fit, minmax(355px, 1fr))` + 2.5rem-gap grid produced at the same width, and tells the
   * skeleton layer (all-events-viewport::before) which rows are rendered so it only fills the gaps a
   * fast scroll leaves before the next rows render.
   */
  @ViewChild('allEventsGridViewport')
  set gridViewport(viewport: CdkVirtualScrollViewport | undefined) {
    this.gridResizeObserver?.disconnect();
    this.gridResizeObserver = undefined;
    this.gridRangeSubscription?.unsubscribe();
    this.gridRangeSubscription = undefined;
    if (!viewport) return;

    const host: HTMLElement = viewport.elementRef.nativeElement;
    this.gridRangeSubscription = viewport.renderedRangeStream.subscribe(({ start, end }) => {
      host.style.setProperty('--sk-hide-start', `${start * this.gridRowHeight}px`);
      host.style.setProperty('--sk-hide-end', `${end * this.gridRowHeight}px`);
    });

    if (typeof ResizeObserver === 'undefined') return;
    this.gridResizeObserver = new ResizeObserver(([entry]) => this.setGridColumns(entry.contentRect.width));
    this.gridResizeObserver.observe(host);
  }

  private setGridColumns(width: number): void {
    const minCard = 355;
    const gap = 40;
    const cols = this.screenWidth <= 600 ? 1 : Math.max(1, Math.floor((width + gap) / (minCard + gap)));
    if (cols === this.gridColumns) return;
    this.ngZone.run(() => {
      this.gridColumns = cols;
      this.cdr.markForCheck();
    });
  }

  get eventRows(): any[][] {
    const list = this.displayedEvents ?? [];
    const cols = this.gridColumns;
    if (this._cachedDisplayedEvents === list && this._cachedCols === cols) {
      return this._cachedRows;
    }
    this._cachedDisplayedEvents = list;
    this._cachedCols = cols;
    const rows: any[][] = [];
    for (let i = 0; i < list.length; i += cols) {
      rows.push(list.slice(i, i + cols));
    }
    this._cachedRows = rows;
    return rows;
  }

  trackByRow(index: number, row: any[]): string {
    return row?.[0]?.id ? `${row[0].id}-${row.length}` : `${index}`;
  }

  /** Grid viewport scroll; loads the next page when the rendered rows near the end (window:scroll also does). */
  onViewportScrolled(viewport: CdkVirtualScrollViewport): void {
    if (this.isLoading || !this.hasMoreData) return;
    const range = viewport.getRenderedRange();
    if (range.end >= this.eventRows.length - 2) {
      this.loadMoreEvents();
    }
  }

  trackByDay(index: number, day: any): string {
    return day?.dateStr ?? `empty-${index}`;
  }
}
